import * as React from 'react';
import {
  TPAComponentsWrapper,
  createRendererWithUniDriver,
} from '../../test/utils';
import { toastDriverFactory } from './Toast.driver';
import { Toast } from '.';
import { ToastSkin } from './types';

describe('Toast', () => {
  const createDriver = createRendererWithUniDriver(toastDriverFactory);

  it('should render', async () => {
    const { driver } = createDriver(<Toast skin={ToastSkin.success} />);
    expect(await driver.exists()).toBe(true);
  });

  it('should use mobile design', async () => {
    const { driver } = createDriver(
      TPAComponentsWrapper({ mobile: true })(
        <Toast skin={ToastSkin.success} data-hook="toast" />,
      ),
      'toast',
    );
    expect(await driver.isMobile()).toBe(true);
  });

  it('should render toast message correctly', async () => {
    const message = 'test toast message';
    const { driver } = createDriver(
      <Toast skin={ToastSkin.success}>{message}</Toast>,
    );
    const messageDriver = await driver.getMessage();
    expect(await messageDriver.exists()).toBeTruthy();
    expect(await messageDriver.text()).toBe(message);
  });

  describe('Animated toast', () => {
    it('should be visible', async () => {
      const { driver } = createDriver(
        <Toast skin={ToastSkin.success} shouldAnimate isShown />,
      );
      expect(await driver.isShown()).toBeTruthy();
    });

    it('should be invisible', async () => {
      const { driver } = createDriver(
        <Toast skin={ToastSkin.success} shouldAnimate isShown={false} />,
      );
      expect(await driver.isShown()).toBeFalsy();
    });
  });

  describe('Close button', () => {
    it('should render WITHOUT close button by default', async () => {
      const { driver } = createDriver(<Toast skin={ToastSkin.success} />);
      expect(await driver.isCloseButtonExists()).toBeFalsy();
    });

    it('should render with close button', async () => {
      const { driver } = createDriver(
        <Toast skin={ToastSkin.success} shouldShowCloseButton />,
      );
      expect(await driver.isCloseButtonExists()).toBeTruthy();
    });

    it('should call onClose callback when on close button click', async () => {
      const onClose = jest.fn();
      const { driver } = createDriver(
        <Toast
          skin={ToastSkin.success}
          shouldShowCloseButton
          onClose={onClose}
        />,
      );
      await (await driver.getCloseButton()).click();
      expect(onClose).toHaveBeenCalledWith(
        expect.objectContaining({
          type: 'click',
        }),
      );
    });

    it('should NOT call onClose callback when click outside close button', async () => {
      const onClose = jest.fn();
      const { driver } = createDriver(
        <Toast
          skin={ToastSkin.success}
          shouldShowCloseButton
          onClose={onClose}
        />,
      );
      await driver.click();
      expect(onClose).not.toHaveBeenCalled();
    });
  });

  describe('Skins', () => {
    it('should render toast with successful skin', async () => {
      const { driver } = createDriver(<Toast skin={ToastSkin.success} />);
      expect(await driver.isSuccess()).toBeTruthy();
      expect(await driver.isError()).toBeFalsy();
      expect(await driver.isStatus()).toBeFalsy();
      expect(await driver.isPreview()).toBeFalsy();
      expect(await driver.exists()).toBe(true);
    });

    it('should render toast with error skin', async () => {
      const { driver } = createDriver(<Toast skin={ToastSkin.error} />);
      expect(await driver.isSuccess()).toBeFalsy();
      expect(await driver.isError()).toBeTruthy();
      expect(await driver.isStatus()).toBeFalsy();
      expect(await driver.isPreview()).toBeFalsy();
      expect(await driver.exists()).toBe(true);
    });

    it('should render toast with status skin', async () => {
      const { driver } = createDriver(<Toast skin={ToastSkin.status} />);
      expect(await driver.isSuccess()).toBeFalsy();
      expect(await driver.isError()).toBeFalsy();
      expect(await driver.isStatus()).toBeTruthy();
      expect(await driver.isPreview()).toBeFalsy();
      expect(await driver.exists()).toBe(true);
    });

    it('should render toast with preview skin', async () => {
      const { driver } = createDriver(<Toast skin={ToastSkin.preview} />);
      expect(await driver.isSuccess()).toBeFalsy();
      expect(await driver.isError()).toBeFalsy();
      expect(await driver.isStatus()).toBeFalsy();
      expect(await driver.isPreview()).toBeTruthy();
      expect(await driver.exists()).toBe(true);
    });
  });
});
